import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
  Cell,
} from 'recharts';
import { ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Trophy } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent } from '@/components/ui/card';
import { MESES } from '@/components/constants/meses';
import { GLASS_CARD } from '@/components/ui/glass';
import { useResumenAnual } from '@/components/hooks/useResumenAnual';
import { useChartColors, useDarkMode, useIsMobile } from './chartHooks';

type Vista = 'dinero' | 'reservas';

interface Props {
  empresaId: string;
  anio: number;
  mes: number;
  onSelectMes: (mes: number, anio: number) => void;
}

interface Punto {
  mes: number;
  label: string;
  nombre: string;
  ingresos: number;
  gastos: number;
  beneficio: number;
  reservas: number;
  ingresosAnt: number;
  gastosAnt: number;
  reservasAnt: number;
  futuro: boolean;
}

const euro = (v: number) =>
  `${v.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €`;

const compacto = new Intl.NumberFormat('es-ES', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const capitalizar = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const variacion = (actual: number, anterior: number) =>
  anterior > 0 ? ((actual - anterior) / anterior) * 100 : null;

const Delta = ({
  valor,
  texto,
  invertido = false,
}: {
  valor: number | null;
  texto: string;
  invertido?: boolean;
}) => {
  if (valor === null) {
    return <span className="text-xs text-gray-400 dark:text-gray-500">—</span>;
  }
  const positivo = valor >= 0;
  const bueno = invertido ? !positivo : positivo;
  const Icono = positivo ? TrendingUp : TrendingDown;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-semibold whitespace-nowrap ${
        bueno
          ? 'text-emerald-600 dark:text-emerald-400'
          : 'text-red-600 dark:text-red-400'
      }`}
    >
      <Icono className="w-3.5 h-3.5" />
      {positivo ? '+' : ''}
      {valor.toFixed(1)}%
      <span className="font-normal text-gray-400 dark:text-gray-500">
        {texto}
      </span>
    </span>
  );
};

const Kpi = ({
  titulo,
  valor,
  pie,
  className = '',
}: {
  titulo: string;
  valor: string;
  pie: React.ReactNode;
  className?: string;
}) => (
  <div className={`rounded-xl bg-gray-50/80 dark:bg-white/5 px-3.5 py-3 flex flex-col gap-1 min-w-0 ${className}`}>
    <span className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-500 dark:text-gray-400 truncate">
      {titulo}
    </span>
    <span className="text-lg sm:text-2xl font-bold tracking-tight text-gray-900 dark:text-white tabular-nums whitespace-nowrap">
      {valor}
    </span>
    <div className="min-h-[18px]">{pie}</div>
  </div>
);

const DashboardResumenAnual = ({
  empresaId,
  anio: anioDashboard,
  mes: mesDashboard,
  onSelectMes,
}: Props) => {
  const { t } = useTranslation();
  const dark = useDarkMode();
  const isMobile = useIsMobile();
  const colors = useChartColors(dark);
  const [vista, setVista] = useState<Vista>('dinero');
  const [anio, setAnio] = useState(anioDashboard);

  useEffect(() => {
    setAnio(anioDashboard);
  }, [anioDashboard]);

  const { loading, mensual, mensualAnterior, primerAnio } = useResumenAnual(
    empresaId,
    anio
  );
  const mes = anio === anioDashboard ? mesDashboard : 0;

  const hoy = new Date();
  const anioActual = hoy.getFullYear();
  const mesActual = hoy.getMonth() + 1;

  const datos: Punto[] = useMemo(
    () =>
      MESES.map((nombre, i) => {
        const n = i + 1;
        const m = mensual.find((x) => x.mes === n);
        const a = mensualAnterior.find((x) => x.mes === n);
        const ingresos = Number(m?.ingresos_mes ?? 0);
        const gastos = Number(m?.gastos_mes ?? 0);
        return {
          mes: n,
          label: nombre.slice(0, 3).toUpperCase(),
          nombre: capitalizar(nombre),
          ingresos,
          gastos,
          beneficio: ingresos - gastos,
          reservas: Number(m?.total_reservas ?? 0),
          ingresosAnt: Number(a?.ingresos_mes ?? 0),
          gastosAnt: Number(a?.gastos_mes ?? 0),
          reservasAnt: Number(a?.total_reservas ?? 0),
          futuro: anio > anioActual || (anio === anioActual && n > mesActual),
        };
      }),
    [mensual, mensualAnterior, anio, anioActual, mesActual]
  );

  const resumen = useMemo(() => {
    const cerrados = datos.filter((d) => !d.futuro);
    const suma = (arr: Punto[], k: keyof Punto) =>
      arr.reduce((acc, d) => acc + (d[k] as number), 0);

    const ingresos = suma(datos, 'ingresos');
    const gastos = suma(datos, 'gastos');
    const reservas = suma(datos, 'reservas');
    const beneficio = ingresos - gastos;

    const mejor = datos.reduce<Punto | null>(
      (best, d) => (d.ingresos > (best?.ingresos ?? 0) ? d : best),
      null
    );

    return {
      ingresos,
      gastos,
      reservas,
      beneficio,
      margen: ingresos > 0 ? (beneficio / ingresos) * 100 : null,
      deltaIngresos: variacion(
        suma(cerrados, 'ingresos'),
        suma(cerrados, 'ingresosAnt')
      ),
      deltaGastos: variacion(
        suma(cerrados, 'gastos'),
        suma(cerrados, 'gastosAnt')
      ),
      deltaReservas: variacion(
        suma(cerrados, 'reservas'),
        suma(cerrados, 'reservasAnt')
      ),
      mejor,
      hayDatos: ingresos > 0 || gastos > 0 || reservas > 0,
    };
  }, [datos]);

  const maxCelda = useMemo(
    () =>
      Math.max(
        1,
        ...datos.map((d) => (vista === 'dinero' ? d.ingresos : d.reservas))
      ),
    [datos, vista]
  );

  const datosGrafica = useMemo(
    () =>
      datos.map((d) =>
        d.futuro
          ? { ...d, ingresos: null, gastos: null, beneficio: null, reservas: null }
          : d
      ),
    [datos]
  );

  const textoVs = t('dashboardAnual.vsYear', { anio: anio - 1 });
  const etiquetaSeleccionada = datos[mes - 1]?.label;

  const TooltipContenido = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const d: Punto = datos[payload[0].payload.mes - 1];
    const filas =
      vista === 'dinero'
        ? [
            { k: t('dashboardCharts.income'), v: euro(d.ingresos), c: colors.income },
            { k: t('dashboardCharts.expenses'), v: euro(d.gastos), c: colors.expenses },
            { k: t('dashboardAnual.profit'), v: euro(d.beneficio), c: colors.profit },
            {
              k: `${t('dashboardCharts.income')} ${anio - 1}`,
              v: euro(d.ingresosAnt),
              c: colors.previous,
            },
            {
              k: `${t('dashboardCharts.expenses')} ${anio - 1}`,
              v: euro(d.gastosAnt),
              c: colors.previous,
            },
          ]
        : [
            { k: t('dashboardAnual.bookings'), v: d.reservas, c: colors.bar },
            { k: t('dashboardAnual.year', { anio: anio - 1 }), v: d.reservasAnt, c: colors.previous },
          ];
    return (
      <div
        style={{
          background: colors.tooltipBg,
          border: `1px solid ${colors.tooltipBorder}`,
          color: colors.tooltipText,
        }}
        className="rounded-xl px-3 py-2 text-xs shadow-lg backdrop-blur min-w-[180px]"
      >
        <div className="font-semibold mb-1.5">
          {d.nombre} {anio}
        </div>
        {filas.map((f) => (
          <div key={f.k} className="flex items-center justify-between gap-4 py-0.5">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ background: f.c }} />
              {f.k}
            </span>
            <span className="font-semibold tabular-nums">{f.v}</span>
          </div>
        ))}
      </div>
    );
  };

  const onBarClick = (d: any) => {
    const n = d?.payload?.mes ?? d?.mes;
    if (n) onSelectMes(n, anio);
  };

  const axisTick = { fill: colors.tickFill, fontSize: isMobile ? 10 : 11 };

  return (
    <Card
      className={`relative isolate overflow-hidden rounded-2xl ${GLASS_CARD} tour-resumen-anual`}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/15"
      />
      <CardContent className={`p-4 sm:p-6 space-y-5 transition-opacity duration-200 ${loading ? 'opacity-60' : ''}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-1 tour-resumen-anio">
              <h2 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white">
                {t('dashboardAnual.titleShort')}
              </h2>
              <div className="flex items-center rounded-xl bg-gray-100/80 dark:bg-white/5 p-0.5 ml-1">
                <button
                  onClick={() => setAnio((a) => a - 1)}
                  disabled={anio <= primerAnio}
                  aria-label={t('dashboardAnual.prevYear')}
                  className="p-1 rounded-lg text-gray-500 dark:text-gray-400 enabled:hover:bg-white dark:enabled:hover:bg-white/10 enabled:hover:text-gray-900 dark:enabled:hover:text-white disabled:opacity-30"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="px-1.5 text-sm font-bold text-gray-900 dark:text-white tabular-nums">
                  {anio}
                </span>
                <button
                  onClick={() => setAnio((a) => a + 1)}
                  disabled={anio >= anioActual}
                  aria-label={t('dashboardAnual.nextYear')}
                  className="p-1 rounded-lg text-gray-500 dark:text-gray-400 enabled:hover:bg-white dark:enabled:hover:bg-white/10 enabled:hover:text-gray-900 dark:enabled:hover:text-white disabled:opacity-30"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              {t('dashboardAnual.clickHint')}
            </p>
          </div>

          <div className="relative flex rounded-xl bg-gray-100/80 dark:bg-white/5 p-1 tour-resumen-vista">
            {(['dinero', 'reservas'] as Vista[]).map((v) => (
              <button
                key={v}
                onClick={() => setVista(v)}
                className={`relative z-10 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                  vista === v
                    ? 'text-white'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                {vista === v && (
                  <motion.span
                    layoutId="vista-anual"
                    className="absolute inset-0 -z-10 rounded-lg bg-[#103254] dark:bg-[#2170b8] shadow"
                    transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                  />
                )}
                {v === 'dinero'
                  ? t('dashboardAnual.money')
                  : t('dashboardAnual.bookings')}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Kpi
            titulo={t('dashboardCharts.income')}
            valor={euro(resumen.ingresos)}
            pie={<Delta valor={resumen.deltaIngresos} texto={textoVs} />}
          />
          <Kpi
            titulo={t('dashboardCharts.expenses')}
            valor={euro(resumen.gastos)}
            pie={<Delta valor={resumen.deltaGastos} texto={textoVs} invertido />}
          />
          <Kpi
            titulo={t('dashboardAnual.profit')}
            valor={euro(resumen.beneficio)}
            pie={
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {t('dashboardAnual.margin')}{' '}
                <strong className="text-gray-700 dark:text-gray-200">
                  {resumen.margen === null ? '—' : `${resumen.margen.toFixed(1)}%`}
                </strong>
              </span>
            }
          />
          <Kpi
            titulo={t('dashboardAnual.bookings')}
            valor={resumen.reservas.toLocaleString('es-ES')}
            pie={<Delta valor={resumen.deltaReservas} texto={textoVs} />}
          />
          <Kpi
            className="col-span-2 lg:col-span-1"
            titulo={t('dashboardAnual.bestMonth')}
            valor={resumen.mejor ? resumen.mejor.nombre : '—'}
            pie={
              resumen.mejor && (
                <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 font-semibold">
                  <Trophy className="w-3.5 h-3.5" />
                  {euro(resumen.mejor.ingresos)}
                </span>
              )
            }
          />
        </div>

        {!resumen.hayDatos ? (
          <div className="h-56 flex items-center justify-center text-sm text-gray-400 dark:text-gray-500">
            {t('dashboardAnual.noData', { anio })}
          </div>
        ) : (
          <div className="-mx-2 overflow-x-auto" data-no-swipe>
            <div className="h-64 sm:h-72 min-w-[560px] px-2">
              <ResponsiveContainer>
                <ComposedChart data={datosGrafica} barGap={3} barCategoryGap="22%">
                  <defs>
                    <linearGradient id="gradReservas" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={colors.bar} stopOpacity={0.45} />
                      <stop offset="100%" stopColor={colors.bar} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} stroke={colors.grid} />
                  <XAxis
                    dataKey="label"
                    tick={axisTick}
                    interval={0}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={axisTick}
                    axisLine={false}
                    tickLine={false}
                    width={40}
                    tickFormatter={(v: number) => compacto.format(v)}
                  />
                  <Tooltip content={<TooltipContenido />} cursor={{ fill: colors.cursor }} />
                  {etiquetaSeleccionada && (
                    <ReferenceLine
                      x={etiquetaSeleccionada}
                      stroke={colors.tickFill}
                      strokeDasharray="3 3"
                      strokeOpacity={0.6}
                    />
                  )}

                  {vista === 'dinero' ? (
                    <>
                      <Bar
                        dataKey="ingresos"
                        fill={colors.income}
                        radius={[6, 6, 0, 0]}
                        cursor="pointer"
                        onClick={onBarClick}
                      >
                        {datos.map((d) => (
                          <Cell
                            key={d.mes}
                            fillOpacity={d.futuro ? 0.25 : d.mes === mes ? 1 : 0.75}
                          />
                        ))}
                      </Bar>
                      <Bar
                        dataKey="gastos"
                        fill={colors.expenses}
                        radius={[6, 6, 0, 0]}
                        cursor="pointer"
                        onClick={onBarClick}
                      >
                        {datos.map((d) => (
                          <Cell
                            key={d.mes}
                            fillOpacity={d.futuro ? 0.25 : d.mes === mes ? 1 : 0.75}
                          />
                        ))}
                      </Bar>
                      <Line
                        dataKey="beneficio"
                        type="monotone"
                        stroke={colors.profit}
                        strokeWidth={2.5}
                        dot={{ r: 3, fill: colors.profit, strokeWidth: 0 }}
                        activeDot={{ r: 5 }}
                      />
                      <Line
                        dataKey="ingresosAnt"
                        type="monotone"
                        stroke={colors.previous}
                        strokeWidth={1.5}
                        strokeDasharray="5 4"
                        dot={false}
                      />
                    </>
                  ) : (
                    <>
                      <Area
                        dataKey="reservas"
                        type="monotone"
                        stroke={colors.bar}
                        strokeWidth={2.5}
                        fill="url(#gradReservas)"
                        dot={{ r: 3, fill: colors.bar, strokeWidth: 0 }}
                        activeDot={{ r: 5 }}
                      />
                      <Line
                        dataKey="reservasAnt"
                        type="monotone"
                        stroke={colors.previous}
                        strokeWidth={1.5}
                        strokeDasharray="5 4"
                        dot={false}
                      />
                    </>
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400">
          {vista === 'dinero' ? (
            <>
              <Leyenda color={colors.income} texto={t('dashboardCharts.income')} />
              <Leyenda color={colors.expenses} texto={t('dashboardCharts.expenses')} />
              <Leyenda color={colors.profit} texto={t('dashboardAnual.profit')} linea />
            </>
          ) : (
            <Leyenda color={colors.bar} texto={t('dashboardAnual.bookings')} />
          )}
          <Leyenda
            color={colors.previous}
            texto={t('dashboardAnual.year', { anio: anio - 1 })}
            linea
            discontinua
          />
        </div>

        <div className="grid grid-cols-6 sm:grid-cols-12 gap-1.5 tour-resumen-meses" data-no-swipe>
          {datos.map((d) => {
            const valor = vista === 'dinero' ? d.ingresos : d.reservas;
            const intensidad = d.futuro ? 0 : valor / maxCelda;
            const seleccionado = d.mes === mes;
            return (
              <button
                key={d.mes}
                onClick={() => onSelectMes(d.mes, anio)}
                title={`${d.nombre} ${anio}`}
                className={`relative overflow-hidden rounded-lg px-1 py-2 text-center transition-all ${
                  seleccionado
                    ? 'ring-2 ring-[#2170b8] ring-offset-1 ring-offset-white dark:ring-offset-zinc-900'
                    : 'hover:ring-1 hover:ring-gray-300 dark:hover:ring-white/20'
                } ${d.futuro ? 'opacity-40' : ''}`}
              >
                <span
                  aria-hidden
                  className="absolute inset-0"
                  style={{
                    background: vista === 'dinero' ? colors.income : colors.bar,
                    opacity: 0.08 + intensidad * 0.5,
                  }}
                />
                <span className="relative block text-[10px] font-semibold text-gray-500 dark:text-gray-400">
                  {d.label}
                </span>
                <span className="relative block text-xs font-bold text-gray-900 dark:text-white tabular-nums">
                  {vista === 'dinero' ? compacto.format(valor) : valor}
                </span>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const Leyenda = ({
  color,
  texto,
  linea = false,
  discontinua = false,
}: {
  color: string;
  texto: string;
  linea?: boolean;
  discontinua?: boolean;
}) => (
  <span className="inline-flex items-center gap-1.5">
    {linea ? (
      <span
        className="w-4 h-0"
        style={{ borderTop: `2px ${discontinua ? 'dashed' : 'solid'} ${color}` }}
      />
    ) : (
      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: color }} />
    )}
    {texto}
  </span>
);

export default DashboardResumenAnual;
